import React from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiMessageCircle, FiInstagram, FiFacebook, FiLinkedin } from 'react-icons/fi';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const socialLinks = [
    { icon: FiFacebook, label: 'Facebook', url: 'https://www.facebook.com/profile.php?id=100093686070283&sfnsn=wa' },
    { icon: FiInstagram, label: 'Instagram', url: 'https://www.instagram.com/sankar_construction1?utm_source=qr&igsh=NG80eHEyNjh0cHhr' },
    { icon: FiLinkedin, label: 'LinkedIn', url: 'https://www.linkedin.com/in/iyyappan-s-3329253b9' },
  ];

  return (
    <footer className="bg-black text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Company Info */}
          <motion.div variants={itemVariants}>
            <h3 className="text-xl font-bold mb-4"><img src="/logo.png" alt="Sankar Logo" className="h-10 w-10 inline-block mr-2 rounded-full" />Sankar</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Transforming dreams into reality with premium construction and design solutions.
            </p>
            <p className="text-gray-500 text-xs">
              © {currentYear} Sankar Designing & Construction. All rights reserved.
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h4 className="font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {['Home', 'Services', 'Projects', 'Contact'].map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase()}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div variants={itemVariants}>
            <h4 className="font-semibold mb-6">Services</h4>
            <ul className="space-y-3">
              {['Residential', 'Commercial', 'Interior Design', 'Renovation'].map((service) => (
                <li key={service}>
                  <span className="text-gray-400 text-sm">
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={itemVariants}>
            <h4 className="font-semibold mb-6">Contact Info</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <FiPhone className="flex-shrink-0 mt-1" size={18} />
                <a href="tel:9841113286" className="text-gray-400 hover:text-white transition-colors text-sm">
                  9841113286
                </a>
              </div>
              <div className="flex items-start gap-3">
                <FiMessageCircle className="flex-shrink-0 mt-1" size={18} />
                <a href="https://wa.me/919150187138" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm">
                  9150187138
                </a>
              </div>
              <div className="flex items-start gap-3">
                <FiMail className="flex-shrink-0 mt-1" size={18} />
                <a href="mailto:hello@sankardesign.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                  iyyappanramana122@gmail.com
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Divider */}
        <div className="border-t border-gray-800 my-8"></div>

        {/* Bottom Footer */}
        <motion.div
          className="flex flex-col md:flex-row items-center justify-between gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-400 text-sm text-center md:text-left">
            Crafted with passion for building spaces that inspire
          </p>

          {/* Social Links */}
          <div className="flex gap-6">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <motion.a
                  key={social.label}
                  href={social.url}
                  title={social.label}
                  className="text-gray-400 hover:text-white transition-colors"
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Icon size={20} />
                </motion.a>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Floating WhatsApp Button */}
      <motion.a
        href="https://wa.me/919150187138"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all z-40"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <FiMessageCircle size={28} />
      </motion.a>

      {/* Floating Call Button */}
      <motion.a
        href="tel:9841113286"
        className="fixed bottom-24 right-6 w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all z-40"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, duration: 0.3 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <FiPhone size={28} />
      </motion.a>
    </footer>
  );
}
